import { supabase } from "@/lib/supabase";

export type BookingType = "experience" | "trip" | "farm" | "farmer";
export type BookingStatus = "new" | "confirmed" | "completed" | "cancelled";

export interface Booking {
  id: number;
  booking_type: string;
  subject: string | null;
  name: string;
  email: string;
  phone: string | null;
  preferred_date: string | null;
  group_size: number | null;
  message: string | null;
  itinerary: string | null;
  source_page: string | null;
  status: string;
  admin_notes: string | null;
  check_in_status: string | null;
  arrival_time: string | null;
  checked_in_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BookingInput {
  booking_type: string;
  subject?: string | null;
  name: string;
  email: string;
  phone?: string | null;
  preferred_date?: string | null;
  group_size?: number | null;
  message?: string | null;
  itinerary?: string | null;
  source_page?: string | null;
}

export type CheckInStatus = "awaiting" | "checked_in" | "no_show";

export const CHECKIN_META: Record<
  string,
  { label: string; badge: string; dot: string; icon: string }
> = {
  awaiting: {
    label: "Awaiting",
    badge: "bg-accent-100 text-accent-900 border-accent-300",
    dot: "bg-accent-500",
    icon: "ri-time-line",
  },
  checked_in: {
    label: "Checked in",
    badge: "bg-primary-100 text-primary-700 border-primary-300",
    dot: "bg-primary-500",
    icon: "ri-checkbox-circle-line",
  },
  no_show: {
    label: "No-show",
    badge: "bg-background-200 text-foreground-600 border-background-300",
    dot: "bg-foreground-500",
    icon: "ri-user-unfollow-line",
  },
};

export const BOOKING_STATUSES: BookingStatus[] = ["new", "confirmed", "completed", "cancelled"];

export const STATUS_META: Record<
  BookingStatus,
  { label: string; dot: string; badge: string }
> = {
  new: {
    label: "New",
    dot: "bg-accent-500",
    badge: "bg-accent-100 text-accent-900 border-accent-300",
  },
  confirmed: {
    label: "Confirmed",
    dot: "bg-primary-500",
    badge: "bg-primary-100 text-primary-700 border-primary-300",
  },
  completed: {
    label: "Completed",
    dot: "bg-secondary-500",
    badge: "bg-secondary-100 text-secondary-700 border-secondary-300",
  },
  cancelled: {
    label: "Cancelled",
    dot: "bg-foreground-400",
    badge: "bg-background-200 text-foreground-600 border-background-300",
  },
};

export const TYPE_LABEL: Record<string, string> = {
  experience: "Experience",
  trip: "Itinerary",
  farm: "Farm",
  farmer: "Farmer",
};

export function statusMeta(status: string) {
  return STATUS_META[(status as BookingStatus)] || STATUS_META.new;
}

export function typeLabel(type: string) {
  return TYPE_LABEL[type] || "Enquiry";
}

export function checkInMeta(status: string | null) {
  return CHECKIN_META[status || "awaiting"] || CHECKIN_META.awaiting;
}

/** Create a new booking from a public enquiry pop-up. Anon insert only. */
export async function createBooking(input: BookingInput): Promise<void> {
  const { error } = await supabase.from("bookings").insert({
    booking_type: input.booking_type,
    subject: input.subject || null,
    name: input.name,
    email: input.email,
    phone: input.phone || null,
    preferred_date: input.preferred_date || null,
    group_size: typeof input.group_size === "number" ? input.group_size : null,
    message: input.message || null,
    itinerary: input.itinerary || null,
    source_page: input.source_page || null,
  });
  if (error) throw error;
}

/** Load every booking (admin only — requires an authenticated session). */
export async function fetchBookings(): Promise<Booking[]> {
  const { data, error } = await supabase
    .from("bookings")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as Booking[]) || [];
}

export async function updateBookingStatus(id: number, status: BookingStatus): Promise<void> {
  const { error } = await supabase
    .from("bookings")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function updateBookingNotes(id: number, adminNotes: string): Promise<void> {
  const { error } = await supabase
    .from("bookings")
    .update({ admin_notes: adminNotes, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function updateCheckInStatus(id: number, status: CheckInStatus): Promise<void> {
  const patch: Record<string, unknown> = {
    check_in_status: status,
    updated_at: new Date().toISOString(),
  };
  if (status === "checked_in") patch.checked_in_at = new Date().toISOString();
  if (status !== "checked_in") patch.checked_in_at = null;
  const { error } = await supabase.from("bookings").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteBooking(id: number): Promise<void> {
  const { error } = await supabase.from("bookings").delete().eq("id", id);
  if (error) throw error;
}

export function formatBookingDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}